import { EMPTY_TEXT } from '@mitchell/react.component.ux';
import { ConvertBytesToMegaBytes, EnterpriseDocStoreService } from "../../common";
import { FileValidationService } from "../../file";
import { PhotoCaptureAllowedMimeTypeConstant } from '../constants';
import { IntelligentPhotoCaptureContextUpdateOperation } from '../enums';
import { attachmentInfo } from './intelligentPhotoCaptureEstimatorUtilities';
import { getErrorSummaryMessages } from './photoCaptureCommonUtilities';
/**
 * Reads a selected file into a data URL for local carousel preview rendering.
 *
 * Used by the estimator thumbnail gallery upload flow to show a client-side
 * preview immediately, without waiting for server upload.
 *
 * @param {File} file - The selected file from the upload input.
 * @returns {Promise<string>} A promise that resolves with a base64 data URL.
 */
export const readFileAsDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result || EMPTY_TEXT);
        reader.onerror = () => reject(new Error('Unable to read selected file.'));
        reader.readAsDataURL(file);
    });
};
/**
 * Filters gallery session attachments by operation type.
 *
 * @param attachments - Session-scoped replacement operations.
 * @param operationTypes - One or more operation types to match.
 * @param exclude - When true, returns items that do not match the provided operation type(s).
 * @returns Filtered gallery attachment operations.
 */
export const filterGalleryAttachmentsByOperationType = (attachments, operationTypes, exclude = false) => {
    const operationTypeSet = new Set(Array.isArray(operationTypes) ? operationTypes : [operationTypes]);
    return attachments.filter((attachment) => {
        const isMatchedOperationType = operationTypeSet.has(attachment.operationType);
        return exclude ? !isMatchedOperationType : isMatchedOperationType;
    });
};
/**
 * Builds lookup structures from gallery attachment operations in a single pass.
 *
 * @param galleryAttachments - Session-scoped replacement operations.
 * @returns Deleted file id set and replacement lookup keyed by file id.
 */
export const buildGalleryAttachmentLookups = (galleryAttachments) => {
    const deletedFileIdSet = new Set();
    const replacementByFileId = new Map();
    galleryAttachments.forEach((attachment) => {
        if (attachment.operationType === IntelligentPhotoCaptureContextUpdateOperation.DELETE) {
            deletedFileIdSet.add(attachment.replacedFileId);
            return;
        }
        replacementByFileId.set(attachment.replacedFileId, attachment);
    });
    return {
        deletedFileIdSet,
        replacementByFileId
    };
};
/**
 * Clamps a carousel index to a valid range.
 *
 * @param index - Requested slide index.
 * @param totalSlides - Total number of slides.
 * @returns A safe in-range index, or 0 when empty.
 */
export const getSafeGalleryCarouselIndex = (index, totalSlides) => {
    if (!totalSlides) {
        return 0;
    }
    return Math.min(Math.max(index, 0), totalSlides - 1);
};
/**
 * Resolves the initial safe carousel index from a selected tile id and lookup map.
 *
 * @param initialSelectedTileId - Incoming tile id from props.
 * @param isReviewMode - Whether review mode is active.
 * @param tileIdToIndex - Tile id to carousel index map.
 * @param totalSlides - Total number of slides.
 * @returns A safe initial index.
 */
export const getSafeInitialEstimatorCarouselIndex = (initialSelectedTileId, isReviewMode, tileIdToIndex, totalSlides) => {
    if (isReviewMode) {
        return 0;
    }
    const parsedInitialTileId = Number(initialSelectedTileId);
    if (!Number.isFinite(parsedInitialTileId)) {
        return 0;
    }
    const mappedCarouselIndex = tileIdToIndex.get(parsedInitialTileId);
    const resolvedInitialCarouselIndex = mappedCarouselIndex !== null && mappedCarouselIndex !== void 0 ? mappedCarouselIndex : 0;
    return getSafeGalleryCarouselIndex(resolvedInitialCarouselIndex, totalSlides);
};
/**
 * Resolves the currently active gallery selection by carousel index.
 *
 * @param galleryItems - Deduplicated gallery items.
 * @param selectedImageIndex - Current carousel index.
 * @returns Active gallery item and its attachment.
 */
export const getActiveVisibleSelection = (galleryItems, selectedImageIndex) => {
    const galleryItem = galleryItems[selectedImageIndex];
    return {
        attachment: galleryItem === null || galleryItem === void 0 ? void 0 : galleryItem.attachment,
        galleryItem
    };
};
/**
 * Resolves the target gallery item and attachment for estimator retake flow.
 *
 * @param initialSelectedTileId - Incoming selected tile id.
 * @param tileIdToIndex - Tile-to-index lookup map.
 * @param galleryItems - Deduplicated gallery items.
 * @param totalSlides - Current number of carousel slides.
 * @returns Retake target information or `undefined` when no valid target exists.
 */
export const getRetakeTargetSelection = (initialSelectedTileId, tileIdToIndex, galleryItems, totalSlides) => {
    var _a;
    const selectedTileId = Number(initialSelectedTileId);
    if (!Number.isFinite(selectedTileId)) {
        return undefined;
    }
    const targetCarouselIndex = (_a = tileIdToIndex.get(selectedTileId)) !== null && _a !== void 0 ? _a : getSafeGalleryCarouselIndex(selectedTileId, totalSlides);
    const targetGalleryItem = galleryItems[targetCarouselIndex];
    const targetAttachment = targetGalleryItem === null || targetGalleryItem === void 0 ? void 0 : targetGalleryItem.attachment;
    const targetFileId = targetAttachment === null || targetAttachment === void 0 ? void 0 : targetAttachment.fileId;
    if (!targetAttachment || !targetFileId || !targetGalleryItem) {
        return undefined;
    }
    return {
        selectedTileId,
        targetCarouselIndex,
        targetGalleryItem,
        targetAttachment,
        targetFileId
    };
};
/**
 * Deduplicates estimator gallery entries by `attachment.fileId` while preserving
 * first-seen ordering and building a tile-to-carousel index lookup map.
 * Only includes attachments whose mimeType matches isImageMimeType.
 *
 * @param {IIntelligentPhotoCaptureEstimatorGalleryItem[]} visibleAttachmentItems -
 *        Current visible gallery entries after soft-delete filtering.
 * @returns {IDeduplicatedIntelligentPhotoCaptureEstimatorGalleryData}
 *          Deduplicated gallery items and tile-to-index mapping.
 */
export const getDeduplicatedEstimatorGalleryData = (visibleAttachmentItems) => {
    const uniqueGalleryItems = [];
    const tileIdToIndex = new Map();
    const fileIdToCarouselIndex = new Map();
    visibleAttachmentItems.forEach((item) => {
        const { attachment, tileId } = item;
        if (!attachment.fileId) {
            return;
        }
        // Only include if mimeType is an allowed image type
        if (!attachmentInfo.isImageMimeType(attachment.mimeType)) {
            return;
        }
        const attachmentFileId = attachment.fileId;
        const existingCarouselIndex = fileIdToCarouselIndex.get(attachmentFileId);
        if (existingCarouselIndex == null) {
            const nextCarouselIndex = uniqueGalleryItems.length;
            uniqueGalleryItems.push(item);
            fileIdToCarouselIndex.set(attachmentFileId, nextCarouselIndex);
            tileIdToIndex.set(tileId, nextCarouselIndex);
            return;
        }
        tileIdToIndex.set(tileId, existingCarouselIndex);
    });
    return {
        galleryItems: uniqueGalleryItems,
        tileIdToIndex
    };
};
/**
 * Maps estimator gallery items into the carousel component model.
 *
 * If a file has a soft replacement in the current session, replacement preview
 * metadata is used for the slide URL, thumbnail, and title.
 *
 * @param {IIntelligentPhotoCaptureEstimatorGalleryItem[]} items - Deduplicated
 *        gallery entries to be rendered by the carousel.
 * @param {Map<string, IIntelligentGalleryAttachmentInfo>} replacementByFileId - Session-scoped
 *        replacement metadata keyed by original attachment file ID.
 * @returns {ICarouselItem[]} Carousel-ready items for UI rendering.
 */
export const buildEstimatorCarouselItems = (items, replacementByFileId) => {
    return items.map(({ attachment }) => {
        const replacement = replacementByFileId.get(attachment.fileId);
        const replacementFile = replacement === null || replacement === void 0 ? void 0 : replacement.file;
        const replacementFileName = replacementFile ? replacementFile.name : undefined;
        return {
            id: attachment.id,
            url: (replacement === null || replacement === void 0 ? void 0 : replacement.previewUrl) || EnterpriseDocStoreService.createFileUrl(attachment.fileId),
            thumbnailUrl: (replacement === null || replacement === void 0 ? void 0 : replacement.previewUrl) || EnterpriseDocStoreService.createFileUrl(attachment.thumbFileId),
            fileType: attachment.mimeType,
            imageTitle: replacementFileName || attachment.imageName || EMPTY_TEXT,
            tagId: `${attachment.attachmentId}`
        };
    });
};
/**
 * Builds an `IIntelligentGalleryAttachmentInfo` object from an attachment and its gallery context.
 * Centralises the repeated inline construction across delete, upload, and retake flows.
 *
 * @param attachment - The source `IPhotoCaptureAttachmentFileInfo` being targeted.
 * @param tileId - The estimator tile ID associated with the attachment.
 * @param operationType - The gallery operation being recorded (DELETE, UPLOAD, RETAKE).
 * @param options - Optional `file` and `previewUrl` for non-delete operations.
 * @returns A fully populated `IIntelligentGalleryAttachmentInfo` object with an ISO timestamp.
 */
export const buildPhotoReplacementInfo = (attachment, tileId, operationType, options) => (Object.assign({ operationType, replacedAttachmentId: attachment.id, replacedFileId: attachment.fileId, replacedTileId: tileId, timestamp: new Date().toISOString() }, options));
/**
 * Validates a file and returns a user-friendly error message if invalid, otherwise undefined.
 * @param file File to validate
 * @param fileSizeLimit Max file size in bytes
 * @returns Error message string or undefined
 */
export const getFileUploadErrorMessage = (file, fileSizeLimit) => {
    const fileUploadError = FileValidationService.validateFile({
        maxFileSize: fileSizeLimit,
        allowedFileTypes: PhotoCaptureAllowedMimeTypeConstant,
        file: file
    });
    if (fileUploadError) {
        const errorMessagesMap = getErrorSummaryMessages(ConvertBytesToMegaBytes(fileSizeLimit !== null && fileSizeLimit !== void 0 ? fileSizeLimit : 0), PhotoCaptureAllowedMimeTypeConstant);
        const errorMessage = errorMessagesMap.get(fileUploadError);
        if (errorMessage) {
            return errorMessage.toString();
        }
        return `The selected file "${file.name}" is invalid and cannot be uploaded.`;
    }
    return undefined;
};
//# sourceMappingURL=intelligentPhotoCaptureEstimatorThumbnailGalleryUtilities.js.map